import React, { useState, useEffect } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { toast } from 'sonner';
import { useAppConfig } from '../context/AppConfigContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HousingsManager = () => {
  const { appConfig } = useAppConfig();
  const companyName = appConfig?.company_name || null;
  const [searchParams, setSearchParams] = useSearchParams();
  const [housings, setHousings] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingHousing, setEditingHousing] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // {housingId, activeMissions, totalMissions}
  const [activeTab, setActiveTab] = useState("particuliers"); // "particuliers" ou "professionnels"
  
  // États pour la recherche et les filtres
  const [searchTerm, setSearchTerm] = useState("");
  const [searching, setSearching] = useState(false);
  const [filterVille, setFilterVille] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterClient, setFilterClient] = useState("");
  
  const [formData, setFormData] = useState({
    client_id: "",
    adresse: "",
    ville: "",
    code_postal: "",
    type_logement: "",
    pseudo: "",
    surface: "",
    duree_estimee: 120,
    creneau_debut: "",
    creneau_fin: "",
    prix_mm_conciergerie: "",
    prix_intervenant: "",
    intervenants_exclusifs: [],
    instructions: "",
    lien_fiche_menage: "", // Lien vers la FICHE D'INSTRUCTIONS MÉNAGE
    gestion_parures: true, // Active par défaut
    desactiver_alerte_parures: false, // Alerte activée par défaut
    parures_incluses_tarif_menage: false,
    tarif_parure: "",
    nb_parures_defaut: "1",
    prix_parure_supplementaire: "",
    prix_parure_manquante: "",
    // Champs iCal
    ical_airbnb_url: "",
    ical_booking_url: "",
    ical_superhote_url: "",
    ical_last_sync: null,
    // Multi-propriétaires
    appartient_a: null, // null=moi, "cyril", "ben"
    telephone_proprietaire: ""
  });

  // Liste des intervenants disponibles (hérité — à migrer vers la liste dynamique des employés)
  const intervenantsDisponibles = [
    "Marion",
    "Magali",
    "Cyril",
    "Ben"
  ];

  const typeLogements = [
    "Logement particulier",
    "Studio",
    "Partie Commune",
    "Aéroport",
    "Bureau",
    "Cabinet",
    "Local commercial",
    "Livraison presse",
    "Livraison linge",
    "Autre"
  ];

  useEffect(() => {
    loadData();
  }, []);

  // Gérer le paramètre edit dans l'URL pour ouvrir automatiquement le modal d'édition
  useEffect(() => {
    const editHousingId = searchParams.get('edit');
    if (editHousingId && housings.length > 0 && !showModal) {
      const housingToEdit = housings.find(h => h.id === editHousingId);
      if (housingToEdit) {
        handleEdit(housingToEdit);
        // Supprimer le paramètre de l'URL après ouverture
        setSearchParams({});
      }
    }
  }, [searchParams, housings, showModal]);

  // Debounced search and filters effect
  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      loadData(searchTerm, filterVille, filterType, filterClient);
    }, 300); // 300ms debounce

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm, filterVille, filterType, filterClient]);

  const loadData = async (search = "", ville = "", type = "", client = "") => {
    setLoading(search === "" && ville === "" && type === "" && client === "" && housings.length === 0);
    setSearching(search !== "" || ville !== "" || type !== "" || client !== "");
    try {
      // Construire l'URL avec les paramètres de recherche et filtres
      let housingsUrl = `${API}/housings`;
      const params = [];
      if (search) params.push(`search=${encodeURIComponent(search)}`);
      if (ville) params.push(`ville=${encodeURIComponent(ville)}`);
      if (type) params.push(`type_logement=${encodeURIComponent(type)}`);
      if (client) params.push(`client_id=${encodeURIComponent(client)}`);
      if (params.length > 0) {
        housingsUrl += `?${params.join('&')}`;
      }
      
      const [housingsResponse, clientsResponse] = await Promise.all([
        axios.get(housingsUrl),
        axios.get(`${API}/clients`)
      ]);
      
      // Trier les clients par ordre alphabétique
      const sortedClients = clientsResponse.data.sort((a, b) => {
        const nameA = `${a.nom} ${a.prenom}`.toLowerCase();
        const nameB = `${b.nom} ${b.prenom}`.toLowerCase();
        return nameA.localeCompare(nameB, 'fr');
      });
      
      // Trier les logements par client puis par adresse
      const sortedHousings = housingsResponse.data.sort((a, b) => {
        const clientA = sortedClients.find(c => c.id === a.client_id);
        const clientB = sortedClients.find(c => c.id === b.client_id);
        
        if (clientA && clientB) {
          const clientNameA = `${clientA.nom} ${clientA.prenom}`.toLowerCase();
          const clientNameB = `${clientB.nom} ${clientB.prenom}`.toLowerCase();
          
          // Comparer d'abord par nom de client
          const clientCompare = clientNameA.localeCompare(clientNameB, 'fr');
          if (clientCompare !== 0) return clientCompare;
          
          // Si même client, comparer par adresse
          return (a.adresse || '').toLowerCase().localeCompare((b.adresse || '').toLowerCase(), 'fr');
        }
        
        return 0;
      });
      
      setHousings(sortedHousings);
      setClients(sortedClients);
    } catch (error) {
      console.error("Erreur lors du chargement:", error);
      toast.info("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setFilterVille("");
    setFilterType("");
    setFilterClient("");
    loadData();
  };

  // Extraire les villes uniques pour le filtre
  const uniqueVilles = [...new Set(housings.map(h => h.ville).filter(Boolean))].sort();

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        surface: formData.surface ? parseInt(formData.surface) : null,
        duree_estimee: parseInt(formData.duree_estimee),
        prix_mm_conciergerie: formData.prix_mm_conciergerie ? parseFloat(formData.prix_mm_conciergerie) : null,
        prix_intervenant: formData.prix_intervenant ? parseFloat(formData.prix_intervenant) : null,
        tarif_parure: formData.tarif_parure ? parseFloat(formData.tarif_parure) : null,
        nb_parures_defaut: formData.nb_parures_defaut ? parseInt(formData.nb_parures_defaut) : 1,
        prix_parure_supplementaire: formData.prix_parure_supplementaire ? parseFloat(formData.prix_parure_supplementaire) : null,
        prix_parure_manquante: formData.prix_parure_manquante ? parseFloat(formData.prix_parure_manquante) : null,
        desactiver_alerte_parures: formData.desactiver_alerte_parures || false
      };

      if (editingHousing) {
        await axios.put(`${API}/housings/${editingHousing.id}`, submitData);
        toast.success("Logement modifié avec succès");
      } else {
        await axios.post(`${API}/housings`, submitData);
        toast.success("Logement créé avec succès");
      }
      setShowModal(false);
      setEditingHousing(null);
      resetForm();
      await loadData();
    } catch (error) {
      console.error("Erreur lors de l'enregistrement:", error);
      const errorMessage = error.response?.data?.detail || "Erreur lors de l'enregistrement du logement";
      toast.error(`${errorMessage}`);
    }
  };

  const handleEdit = (housing) => {
    setEditingHousing(housing);
    setFormData({
      client_id: housing.client_id,
      adresse: housing.adresse,
      ville: housing.ville,
      code_postal: housing.code_postal,
      type_logement: housing.type_logement,
      pseudo: housing.pseudo || "",
      surface: housing.surface ? housing.surface.toString() : "",
      duree_estimee: housing.duree_estimee,
      creneau_debut: housing.creneau_debut || "",
      creneau_fin: housing.creneau_fin || "",
      prix_mm_conciergerie: housing.prix_mm_conciergerie ? housing.prix_mm_conciergerie.toString() : "",
      prix_intervenant: housing.prix_intervenant ? housing.prix_intervenant.toString() : "",
      intervenants_exclusifs: housing.intervenants_exclusifs || [],
      instructions: housing.instructions || "",
      lien_fiche_menage: housing.lien_fiche_menage || "",
      gestion_parures: housing.gestion_parures !== false, // true par défaut
      desactiver_alerte_parures: housing.desactiver_alerte_parures || false,
      parures_incluses_tarif_menage: housing.parures_incluses_tarif_menage || false,
      tarif_parure: housing.tarif_parure ? housing.tarif_parure.toString() : "",
      nb_parures_defaut: housing.nb_parures_defaut ? housing.nb_parures_defaut.toString() : "1",
      prix_parure_supplementaire: housing.prix_parure_supplementaire ? housing.prix_parure_supplementaire.toString() : "",
      prix_parure_manquante: housing.prix_parure_manquante ? housing.prix_parure_manquante.toString() : "",
      // Champs iCal
      ical_airbnb_url: housing.ical_airbnb_url || "",
      ical_booking_url: housing.ical_booking_url || "",
      ical_superhote_url: housing.ical_superhote_url || "",
      ical_last_sync: housing.ical_last_sync || null,
      // Multi-propriétaires
      appartient_a: housing.appartient_a || null,
      telephone_proprietaire: housing.telephone_proprietaire || ""
    });
    setShowModal(true);
  };

  const handleDelete = async (housingId) => {
    try {
      // Première tentative de suppression (sans force)
      await axios.delete(`${API}/housings/${housingId}`);
      toast.success("Logement supprimé avec succès");
      await loadData();
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      
      // Si erreur 409 = missions existent, afficher modal de confirmation
      if (error.response?.status === 409) {
        const errorData = error.response?.data?.detail;
        const activeMissions = errorData?.active_missions || 0;
        const totalMissions = errorData?.total_missions || 0;
        
        // Afficher le modal de confirmation
        setDeleteConfirm({
          housingId,
          activeMissions,
          totalMissions
        });
      } else {
        // Autre type d'erreur
        const errorMessage = error.response?.data?.detail || "Erreur lors de la suppression du logement";
        toast.error(`${errorMessage}`);
      }
    }
  };
  
  const confirmForceDelete = async () => {
    if (!deleteConfirm) return;
    
    try {
      // Suppression forcée avec missions en cascade
      await axios.delete(`${API}/housings/${deleteConfirm.housingId}?force=true`);
      toast.success(`Logement et ${deleteConfirm.totalMissions} mission(s) supprimés avec succès`);
      setDeleteConfirm(null);
      await loadData();
    } catch (error) {
      console.error("Erreur lors de la suppression forcée:", error);
      const errorMessage = error.response?.data?.detail || "Erreur lors de la suppression du logement";
      toast.error(`${errorMessage}`);
      setDeleteConfirm(null);
    }
  };

  const resetForm = () => {
    setFormData({
      client_id: "",
      adresse: "",
      ville: "",
      code_postal: "",
      type_logement: "",
      pseudo: "",
      surface: "",
      duree_estimee: 120,
      creneau_debut: "",
      creneau_fin: "",
      prix_mm_conciergerie: "",
      prix_intervenant: "",
      intervenants_exclusifs: [],
      instructions: "",
      lien_fiche_menage: "",
      gestion_parures: true, // Active par défaut
      desactiver_alerte_parures: false, // Alerte activée par défaut
      parures_incluses_tarif_menage: false,
      tarif_parure: "",
      nb_parures_defaut: "1",
      prix_parure_supplementaire: "",
      prix_parure_manquante: "",
      // Champs iCal
      ical_airbnb_url: "",
      ical_booking_url: "",
      ical_superhote_url: "",
      ical_last_sync: null,
      // Multi-propriétaires
      appartient_a: null
    });
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? `${client.prenom} ${client.nom}` : "Client inconnu";
  };

  // Gérer le changement des intervenants exclusifs
  const handleIntervenantExclusifToggle = (intervenant) => {
    setFormData(prev => {
      const currentList = prev.intervenants_exclusifs || [];
      const isSelected = currentList.includes(intervenant);
      
      if (isSelected) {
        // Retirer l'intervenant de la liste
        return {
          ...prev,
          intervenants_exclusifs: currentList.filter(i => i !== intervenant)
        };
      } else {
        // Ajouter l'intervenant à la liste
        return {
          ...prev,
          intervenants_exclusifs: [...currentList, intervenant]
        };
      }
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner"></div>
        <span className="ml-2 text-gray-600">Chargement des logements...</span>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto py-6 px-4" data-testid="housings-manager">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">🏘️ Logements et Lieux PRO</h1>
            <p className="text-gray-600 mt-2">
              Gérez les logements de vos clients particuliers et professionnels
            </p>
          </div>
        </div>
      </div>

      {/* Alerte si pas de clients */}
      {clients.length === 0 && (
        <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-md p-4">
          <div className="flex">
            <div className="text-yellow-400 text-xl mr-3">⚠️</div>
            <div>
              <h3 className="text-sm font-medium text-yellow-800">
                Aucun client disponible
              </h3>
              <div className="mt-2 text-sm text-yellow-700">
                <p>
                  Vous devez d'abord créer des clients avant d'ajouter des logements.
                </p>
                <a href="/clients" className="font-medium underline hover:text-yellow-600">
                  Aller à la gestion des clients →
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Onglets Logements Particuliers / Lieux PRO */}
      {housings.length > 0 && (
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex justify-center space-x-16">
              <button
                onClick={() => setActiveTab("particuliers")}
                className={`${
                  activeTab === "particuliers"
                    ? "border-blue-500 text-blue-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                } whitespace-nowrap py-6 px-4 border-b-4 font-bold text-2xl flex items-center space-x-3 transition-all`}
              >
                <span className="text-3xl">🏠</span>
                <span>Logements Particuliers</span>
                <span className="ml-3 bg-blue-100 text-blue-600 py-1.5 px-4 rounded-full text-lg font-bold">
                  {housings.filter(h => {
                    const client = clients.find(c => c.id === h.client_id);
                    return client && (client.type_client || 'particulier') === 'particulier';
                  }).length}
                </span>
              </button>
              <button
                onClick={() => setActiveTab("professionnels")}
                className={`${
                  activeTab === "professionnels"
                    ? "border-purple-500 text-purple-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                } whitespace-nowrap py-6 px-4 border-b-4 font-bold text-2xl flex items-center space-x-3 transition-all`}
              >
                <span className="text-3xl">🏢</span>
                <span>Lieux PRO</span>
                <span className="ml-3 bg-purple-100 text-purple-600 py-1.5 px-4 rounded-full text-lg font-bold">
                  {housings.filter(h => {
                    const client = clients.find(c => c.id === h.client_id);
                    return client && (client.type_client || 'particulier') === 'professionnel';
                  }).length}
                </span>
              </button>
            </nav>
          </div>
        </div>
      )}

      {/* Barre de recherche et filtres - Regroupée en une seule */}
      <div className="mb-6 bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        {/* Recherche */}
        <div className="mb-4">
          <div className="flex items-center space-x-3">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="🔍 Rechercher par pseudo, adresse, ville ou instructions..."
                className="block w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  title="Effacer la recherche"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            {searching && (
              <div className="flex items-center text-sm text-gray-500">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500 mr-2"></div>
                Recherche...
              </div>
            )}
          </div>
        </div>

        {/* Filtres rapides */}
        <div className="flex flex-wrap gap-3">
          <select
            value={filterVille}
            onChange={(e) => setFilterVille(e.target.value)}
            className="form-select text-sm rounded-lg border-gray-300"
          >
            <option value="">🏙️ Toutes les villes</option>
            {uniqueVilles.map(ville => (
              <option key={ville} value={ville}>{ville}</option>
            ))}
          </select>

          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="form-select text-sm rounded-lg border-gray-300"
          >
            <option value="">🏠 Tous les types</option>
            {typeLogements.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>

          <select
            value={filterClient}
            onChange={(e) => setFilterClient(e.target.value)}
            className="form-select text-sm rounded-lg border-gray-300"
          >
            <option value="">👤 Tous les clients</option>
            {clients
              .sort((a, b) => (a.nom || '').localeCompare(b.nom || ''))
              .map(client => (
                <option key={client.id} value={client.id}>
                  {client.nom}
                </option>
              ))}
          </select>

          {(searchTerm || filterVille || filterType || filterClient) && (
            <button
              onClick={handleClearFilters}
              className="px-4 py-2 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors"
            >
              ❌ Tout effacer
            </button>
          )}
        </div>

        {/* Résultats */}
        {(searchTerm || filterVille || filterType || filterClient) && (
          <div className="mt-3 text-sm text-gray-600">
            {housings.length} résultat{housings.length > 1 ? 's' : ''} trouvé{housings.length > 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* Liste des logements groupés par client */}
      <div className="card">
        <div className="card-header flex justify-between items-center">
          <h2 className="text-lg font-semibold text-gray-900">
            {activeTab === "particuliers" ? "🏠 Logements Particuliers" : "🏢 Lieux PRO"} ({
              housings.filter(h => {
                const client = clients.find(c => c.id === h.client_id);
                return client && (client.type_client || 'particulier') === activeTab.replace('professionnels', 'professionnel').replace('particuliers', 'particulier');
              }).length
            })
          </h2>
          <button
            onClick={() => {
              resetForm();
              setEditingHousing(null);
              setShowModal(true);
            }}
            className="btn-primary"
            data-testid="add-housing-btn"
          >
            {activeTab === "particuliers" ? "➕ Nouveau logement" : "➕ Nouveau lieu"}
          </button>
        </div>
        <div className="card-body">
          {housings.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">🏘️</div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Aucun logement enregistré
              </h3>
              <p className="text-gray-500 mb-4">
                Commencez par ajouter votre premier logement
              </p>
              {clients.length > 0 && (
                <button
                  onClick={() => {
                    resetForm();
                    setEditingHousing(null);
                    setShowModal(true);
                  }}
                  className="btn-primary"
                >
                  Ajouter un logement
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {(() => {
                // Filtrer les logements selon l'onglet actif
                const filteredHousings = housings.filter(housing => {
                  const client = clients.find(c => c.id === housing.client_id);
                  if (!client) return false;
                  const clientType = client.type_client || 'particulier';
                  return activeTab === 'particuliers' ? clientType === 'particulier' : clientType === 'professionnel';
                });

                // Grouper les logements par client
                const groupedByClient = {};
                filteredHousings.forEach(housing => {
                  if (!groupedByClient[housing.client_id]) {
                    groupedByClient[housing.client_id] = [];
                  }
                  groupedByClient[housing.client_id].push(housing);
                });

                // Couleurs pastel pour les groupes
                const colors = [
                  'from-blue-50 to-blue-100 border-blue-200',
                  'from-green-50 to-green-100 border-green-200',
                  'from-purple-50 to-purple-100 border-purple-200',
                  'from-pink-50 to-pink-100 border-pink-200',
                  'from-yellow-50 to-yellow-100 border-yellow-200',
                  'from-indigo-50 to-indigo-100 border-indigo-200',
                  'from-red-50 to-red-100 border-red-200',
                  'from-teal-50 to-teal-100 border-teal-200',
                ];

                return Object.keys(groupedByClient)
                  .sort((a, b) => {
                    // Tri alphabétique par prénom du client
                    const clientA = clients.find(c => c.id === a);
                    const clientB = clients.find(c => c.id === b);
                    const prenomA = clientA ? clientA.prenom.toLowerCase() : '';
                    const prenomB = clientB ? clientB.prenom.toLowerCase() : '';
                    return prenomA.localeCompare(prenomB);
                  })
                  .map((clientId, clientIndex) => {
                  const clientHousings = groupedByClient[clientId];
                  const client = clients.find(c => c.id === clientId);
                  const clientName = client ? `${client.prenom} ${client.nom}` : 'Client inconnu';
                  const colorClass = colors[clientIndex % colors.length];

                  return (
                    <div key={clientId} className={`bg-gradient-to-r ${colorClass} border-2 rounded-xl overflow-hidden`}>
                      {/* En-tête client */}
                      <div className="px-4 py-2 border-b-2 border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <span className="text-3xl">👤</span>
                            <div>
                              <h3 className="text-xl font-bold text-gray-900">{clientName}</h3>
                              <p className="text-sm text-gray-600">
                                {clientHousings.length} logement{clientHousings.length > 1 ? 's' : ''}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Logements du client */}
                      <div className="p-2">
                        <div className="grid grid-cols-1 gap-2">
                          {clientHousings.map((housing) => {
                            // Définir la bordure selon le propriétaire
                            const ownerBorderClass = housing.appartient_a === 'cyril' 
                              ? 'border-l-4 border-l-blue-500' 
                              : housing.appartient_a === 'ben' 
                                ? 'border-l-4 border-l-violet-500' 
                                : '';
                            const ownerBadge = housing.appartient_a === 'cyril' 
                              ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800 ml-2">Gestion Cyril</span>
                              : housing.appartient_a === 'ben' 
                                ? <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-violet-100 text-violet-800 ml-2">Gestion Ben</span>
                                : null;
                            
                            return (
                            <div key={housing.id} className={`bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow ${ownerBorderClass}`} data-testid={`housing-row-${housing.id}`}>
                              <div className="p-2 bg-gray-50 rounded">
                                <div className="flex justify-between items-start">
                                  {/* Informations principales */}
                                  <div className="flex-1">
                                    <div className="flex items-center space-x-3 mb-2">
                                      {housing.pseudo && (
                                        <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-purple-100 text-purple-800">
                                          🎤 {housing.pseudo}
                                        </span>
                                      )}
                                      <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                        {housing.type_logement}
                                      </span>
                                      {ownerBadge}
                                    </div>
                                    
                                    <div className="space-y-1">
                                      <div className="flex items-start space-x-2">
                                        <span className="text-gray-500">📍</span>
                                        <div>
                                          <p className="font-medium text-gray-900">{housing.adresse}</p>
                                          <p className="text-sm text-gray-600">{housing.ville} {housing.code_postal}</p>
                                        </div>
                                      </div>
                                      
                                      <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-600">
                                        {housing.surface && (
                                          <span className="flex items-center space-x-1">
                                            <span>📏</span>
                                            <span>{housing.surface} m²</span>
                                          </span>
                                        )}
                                        <span className="flex items-center space-x-1">
                                          <span>⏱️</span>
                                          <span>{housing.duree_estimee} min</span>
                                        </span>
                                        {housing.prix_mm_conciergerie && (
                                          <span className="flex items-center space-x-1">
                                            <span>💰</span>
                                            <span>{housing.prix_mm_conciergerie}€ {housing.appartient_a === 'cyril' ? '(Cyril)' : housing.appartient_a === 'ben' ? '(Ben)' : ''}</span>
                                          </span>
                                        )}
                                      </div>
                                      
                                      {/* Affichage des intervenants exclusifs */}
                                      {housing.intervenants_exclusifs && housing.intervenants_exclusifs.length > 0 && (
                                        <div className="mt-3 p-2 bg-blue-50 rounded-lg border border-blue-200">
                                          <div className="flex items-start space-x-2">
                                            <span className="text-blue-600">👤</span>
                                            <div className="flex-1">
                                              <p className="text-xs font-semibold text-blue-800">Intervenants exclusifs :</p>
                                              <p className="text-xs text-blue-700 mt-1">
                                                {housing.intervenants_exclusifs.join(", ")}
                                              </p>
                                            </div>
                                          </div>
                                        </div>
                                      )}
                                      
                                      {/* Affichage du lien vers la fiche ménage */}
                                      {housing.lien_fiche_menage && (
                                        <div className="mt-3">
                                          <a
                                            href={housing.lien_fiche_menage}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="inline-flex items-center px-3 py-1.5 bg-green-100 hover:bg-green-200 text-green-700 rounded-lg text-sm transition-colors"
                                            data-testid={`fiche-menage-link-${housing.id}`}
                                          >
                                            📄 Voir la fiche ménage
                                          </a>
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  {/* Actions */}
                                  <div className="flex space-x-2 ml-4">
                                    <button
                                      onClick={() => handleEdit(housing)}
                                      className="btn-secondary text-sm px-3 py-2"
                                      data-testid={`edit-housing-${housing.id}`}
                                    >
                                      ✏️ Modifier
                                    </button>
                                    <button
                                      onClick={() => handleDelete(housing.id)}
                                      className="btn-danger text-sm px-3 py-2"
                                      data-testid={`delete-housing-${housing.id}`}
                                    >
                                      🗑️ Supprimer
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </div>
      </div>

      {/* Modal de création/édition */}
      {showModal && (
        <div className="modal-overlay" data-testid="housing-modal">
          <div className="modal-content">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingHousing 
                  ? (activeTab === "professionnels" ? "Modifier le lieu" : "Modifier le logement")
                  : (activeTab === "professionnels" ? "Nouveau lieu" : "Nouveau logement")
                }
              </h3>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingHousing(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
                data-testid="close-modal-btn"
              >
                ❌
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Client *
                </label>
                <select
                  required
                  value={formData.client_id}
                  onChange={(e) => setFormData(prev => ({...prev, client_id: e.target.value}))}
                  className="form-select"
                  data-testid="housing-client-select"
                >
                  <option value="">Sélectionner un client</option>
                  {clients
                    .filter(client => {
                      // Filtrer les clients selon l'onglet actif
                      const clientType = client.type_client || 'particulier';
                      if (activeTab === 'particuliers') {
                        return clientType === 'particulier';
                      } else {
                        return clientType === 'professionnel';
                      }
                    })
                    .map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.prenom} {client.nom}
                      </option>
                    ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gestion
                </label>
                <select
                  value={formData.appartient_a || ''}
                  onChange={(e) => setFormData(prev => ({...prev, appartient_a: e.target.value || null}))}
                  className="form-select"
                  data-testid="housing-gestion-select"
                >
                  <option value="">{companyName || 'Ma conciergerie'}</option>
                  <option value="cyril">Gestion Cyril</option>
                  <option value="ben">Gestion Ben</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tel. proprietaire (WhatsApp)
                </label>
                <input
                  type="tel"
                  value={formData.telephone_proprietaire}
                  onChange={(e) => setFormData(prev => ({...prev, telephone_proprietaire: e.target.value}))}
                  placeholder="06 12 34 56 78"
                  className="form-input"
                  data-testid="housing-tel-proprietaire"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Adresse *
                </label>
                <input
                  type="text"
                  required
                  value={formData.adresse}
                  onChange={(e) => setFormData(prev => ({...prev, adresse: e.target.value}))}
                  className="form-input"
                  placeholder="123 rue de la Paix"
                  data-testid="housing-adresse-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ville *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.ville}
                    onChange={(e) => setFormData(prev => ({...prev, ville: e.target.value}))}
                    className="form-input"
                    data-testid="housing-ville-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Code postal *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.code_postal}
                    onChange={(e) => setFormData(prev => ({...prev, code_postal: e.target.value}))}
                    className="form-input"
                    data-testid="housing-code-postal-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type de lieu *
                  </label>
                  <select
                    required
                    value={formData.type_logement}
                    onChange={(e) => setFormData(prev => ({...prev, type_logement: e.target.value}))}
                    className="form-select"
                    data-testid="housing-type-select"
                  >
                    <option value="">Sélectionner un type</option>
                    {typeLogements.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pseudo/Alias vocal 🎤
                  </label>
                  <input
                    type="text"
                    value={formData.pseudo}
                    onChange={(e) => setFormData(prev => ({...prev, pseudo: e.target.value}))}
                    className="form-input"
                    placeholder="Ex: Client Marais, Appartement Bastille..."
                    data-testid="housing-pseudo-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Laissez vide pour génération automatique
                  </p>
                </div>
              </div>

              {/* Champs cachés pour les lieux PRO */}
              {(() => {
                // Détermine si c'est un lieu PRO
                // - En création : basé sur l'onglet actif
                // - En modification : basé sur le type_client du client associé
                let isLieuPro = false;
                
                if (editingHousing) {
                  // Mode édition : vérifier le type du client
                  const client = clients.find(c => c.id === formData.client_id);
                  isLieuPro = client && (client.type_client === 'professionnel');
                } else {
                  // Mode création : basé sur l'onglet actif
                  isLieuPro = activeTab === 'professionnels';
                }
                
                // Affiche ces champs seulement pour les logements particuliers
                if (isLieuPro) {
                  return null;
                }
                
                return (
                  <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Surface (m²)
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.surface}
                    onChange={(e) => setFormData(prev => ({...prev, surface: e.target.value}))}
                    className="form-input"
                    data-testid="housing-surface-input"
                  />
                </div>
                <div></div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Durée estimée (minutes) *
                  </label>
                  <input
                    type="number"
                    required
                    min="30"
                    value={formData.duree_estimee}
                    onChange={(e) => setFormData(prev => ({...prev, duree_estimee: parseInt(e.target.value) || 120}))}
                    className="form-input"
                    data-testid="housing-duree-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Créneau début (HH:MM)
                  </label>
                  <input
                    type="time"
                    value={formData.creneau_debut}
                    onChange={(e) => setFormData(prev => ({...prev, creneau_debut: e.target.value}))}
                    className="form-input"
                    data-testid="housing-creneau-debut-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Créneau fin (HH:MM)
                  </label>
                  <input
                    type="time"
                    value={formData.creneau_fin}
                    onChange={(e) => setFormData(prev => ({...prev, creneau_fin: e.target.value}))}
                    className="form-input"
                    data-testid="housing-creneau-fin-input"
                  />
                </div>
              </div>

              {/* Intervenants Exclusifs */}
              <div className="bg-blue-50 p-4 rounded-lg border-2 border-blue-300 mt-4">
                <label className="block text-sm font-medium text-gray-900 mb-3">
                  👤 Ménage exclusivement effectué par...
                </label>
                <p className="text-xs text-gray-600 mb-3">
                  Sélectionnez un ou plusieurs intervenants autorisés pour ce logement. Si aucun n'est sélectionné, tous les intervenants peuvent prendre ce logement.
                </p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {intervenantsDisponibles.map(intervenant => (
                    <label key={intervenant} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.intervenants_exclusifs.includes(intervenant)}
                        onChange={() => handleIntervenantExclusifToggle(intervenant)}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">{intervenant}</span>
                    </label>
                  ))}
                </div>
                {formData.intervenants_exclusifs.length > 0 && (
                  <div className="mt-3 p-2 bg-blue-100 rounded">
                    <p className="text-xs text-blue-800">
                      <strong>Intervenants exclusifs sélectionnés :</strong> {formData.intervenants_exclusifs.join(", ")}
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      ⚠️ Le champ "Prix Intervenant" sera désactivé car le tarif dépend du prestataire exclusif.
                    </p>
                  </div>
                )}
              </div>
                </>
                );
              })()}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {formData.appartient_a === 'cyril' ? 'Prix Gestion Cyril (€)' : formData.appartient_a === 'ben' ? 'Prix Gestion Ben (€)' : `Prix ${companyName || 'conciergerie'} (€)`}
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.prix_mm_conciergerie}
                    onChange={(e) => setFormData(prev => ({...prev, prix_mm_conciergerie: e.target.value}))}
                    className="form-input"
                    placeholder="0.00"
                    data-testid="housing-prix-mm-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Prix Intervenant (€)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.prix_intervenant}
                    onChange={(e) => setFormData(prev => ({...prev, prix_intervenant: e.target.value}))}
                    className={`form-input ${formData.intervenants_exclusifs.length > 0 ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    placeholder="0.00"
                    disabled={formData.intervenants_exclusifs.length > 0}
                    data-testid="housing-prix-intervenant-input"
                  />
                  {formData.intervenants_exclusifs.length > 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      Désactivé car des intervenants exclusifs sont sélectionnés
                    </p>
                  )}
                </div>
              </div>

              {/* Section Parures - Conditionnelle selon le type de client */}
              {(() => {
                const client = clients.find(c => c.id === formData.client_id);
                const isClientPro = client && (client.type_client === 'professionnel');
                
                // Si client PRO, afficher uniquement un message informatif
                if (isClientPro) {
                  return (
                    <div className="bg-purple-50 p-4 rounded-lg border-2 border-purple-300 mt-4">
                      <p className="text-sm text-purple-800">
                        ℹ️ <strong>Client professionnel</strong> - La gestion des parures est automatiquement désactivée pour les lieux professionnels.
                      </p>
                    </div>
                  );
                }
                
                // Si client particulier, afficher toute la section parures
                return (
                  <>
                    <div>
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!formData.gestion_parures}
                          onChange={(e) => setFormData(prev => ({...prev, gestion_parures: !e.target.checked}))}
                          className="w-5 h-5 text-red-600 rounded focus:ring-red-500"
                        />
                        <span className="text-base font-bold text-gray-900">
                          🚫 Pas de gestion de parures pour ce logement
                        </span>
                      </label>
                      <p className="text-xs text-gray-600 mt-2 ml-8">
                        Cochez cette option pour les logements professionnels ou clients qui n'utilisent pas de parures.
                        <br />
                        <strong>L'employé ne verra aucun champ lié aux parures dans son interface.</strong>
                      </p>
                    </div>

                    {/* Option pour désactiver l'alerte parures sales */}
                    <div className="mt-4">
                      <label className="flex items-center space-x-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={formData.desactiver_alerte_parures}
                          onChange={(e) => setFormData(prev => ({...prev, desactiver_alerte_parures: e.target.checked}))}
                          className="w-5 h-5 text-orange-600 rounded focus:ring-orange-500"
                        />
                        <span className="text-base font-bold text-gray-900">
                          🔕 Désactiver l'alerte "parures sales non renseignées"
                        </span>
                      </label>
                      <p className="text-xs text-gray-600 mt-2 ml-8">
                        Cochez cette option pour vos propres logements ou lieux PRO où l'alerte n'est pas nécessaire.
                        <br />
                        <strong>Ce logement n'apparaîtra plus dans la liste des alertes parures.</strong>
                      </p>
                    </div>

                    {/* Toute la section parures - Visible uniquement si gestion_parures est active */}
                    {formData.gestion_parures && (
                      <>
                  {/* Checkbox : Parures incluses dans tarif ménage */}
                  <div className="bg-yellow-50 p-4 rounded-lg border-2 border-yellow-300 mt-4">
                    <label className="flex items-center space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.parures_incluses_tarif_menage}
                        onChange={(e) => setFormData(prev => ({...prev, parures_incluses_tarif_menage: e.target.checked}))}
                        className="w-5 h-5 text-yellow-600 rounded focus:ring-yellow-500"
                      />
                      <span className="text-base font-bold text-gray-900">
                        ✅ Parures incluses dans le tarif ménage
                      </span>
                    </label>
                    <p className="text-xs text-gray-600 mt-2 ml-8">
                      Cochez cette option si le prix du ménage inclut déjà les parures sales.
                    </p>
                    
                    {/* Champs visibles uniquement si la case est cochée */}
                    {formData.parures_incluses_tarif_menage && (
                      <div className="mt-4 ml-8 space-y-3">
                        <div className="bg-white p-3 rounded border border-yellow-300">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            🛏️ Nombre de parures incluses dans le tarif
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={formData.nb_parures_defaut}
                            onChange={(e) => setFormData(prev => ({...prev, nb_parures_defaut: e.target.value}))}
                            className="form-input w-32"
                            placeholder="1"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Par défaut : 1 parure incluse. Modifiable selon le logement.
                          </p>
                        </div>
                        
                        <div className="bg-white p-3 rounded border border-yellow-300">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            💰 Prix par parure supplémentaire (€)
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={formData.prix_parure_supplementaire}
                            onChange={(e) => setFormData(prev => ({...prev, prix_parure_supplementaire: e.target.value}))}
                            className="form-input w-32"
                            placeholder="0.00"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Montant ajouté automatiquement pour chaque parure au-delà du nombre inclus.
                          </p>
                        </div>
                        
                        <div className="p-3 bg-yellow-100 rounded border border-yellow-400">
                          <p className="text-xs font-semibold text-yellow-900 mb-1">⚠️ Important :</p>
                          <p className="text-xs text-yellow-800">
                            Le système ajoutera automatiquement +{formData.prix_parure_supplementaire || 'X'}€ pour chaque parure récupérée au-delà de {formData.nb_parures_defaut || 1}.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Section Parures Sales - Conditionnelle (si parures NON incluses) */}
                  {!formData.parures_incluses_tarif_menage && (
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border-2 border-blue-200 mt-4">
                  <h4 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                    🛏️ Paramètres Parures Sales
                    <span className="ml-2 text-xs font-normal text-gray-600">(Calcul automatique)</span>
                  </h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Indiquez simplement le tarif par parure. Le calcul se fera automatiquement : <strong>nb_parures × tarif = total</strong>
                  </p>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      💰 Tarif 1 parure sale (€)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.tarif_parure}
                      onChange={(e) => setFormData(prev => ({...prev, tarif_parure: e.target.value}))}
                      className="form-input"
                      placeholder="Ex: 17.00"
                    />
                    <p className="text-xs text-gray-500 mt-1">Prix appliqué à chaque parure</p>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      ➕ Tarif parure supplémentaire (€) <span className="text-gray-400 text-xs">(optionnel)</span>
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.prix_parure_supplementaire}
                      onChange={(e) => setFormData(prev => ({...prev, prix_parure_supplementaire: e.target.value}))}
                      className="form-input"
                      placeholder="Ex: 20.00"
                    />
                    <p className="text-xs text-gray-500 mt-1">Si tarif différent au-delà de la 1ère</p>
                  </div>
                </div>
                
                {/* Exemple de calcul simplifié */}
                {formData.tarif_parure && (
                  <div className="mt-4 p-3 bg-white rounded border border-blue-200">
                    <p className="text-xs font-semibold text-gray-700 mb-2">💡 Exemple de calcul :</p>
                    <div className="text-xs text-gray-600 space-y-1">
                      <p>• <strong>Formule :</strong> nb_parures × {formData.tarif_parure}€ = total</p>
                    </div>
                  </div>
                )}
              </div>
                  )}
                      </>
                    )}
                </>
                );
              })()}

              {/* Section iCal - Seulement pour logements particuliers */}
              {(() => {
                const client = clients.find(c => c.id === formData.client_id);
                const isLieuPro = client && (client.type_client === 'professionnel');
                
                if (isLieuPro) {
                  return null; // Pas d'iCal pour les lieux PRO
                }
                
                return (
                  <div className="bg-purple-50 p-4 rounded-lg border-2 border-purple-300 mt-4">
                    <h3 className="font-semibold text-purple-900 mb-3 flex items-center">
                      📅 Synchronisation iCal (Airbnb, Booking...)
                    </h3>
                    <p className="text-xs text-purple-700 mb-4">
                      Les check-outs seront automatiquement détectés et transformés en missions de ménage.
                    </p>
                    
                    <div className="grid grid-cols-1 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          🔗 URL iCal Airbnb
                        </label>
                        <input
                          type="url"
                          value={formData.ical_airbnb_url || ''}
                          onChange={(e) => setFormData(prev => ({...prev, ical_airbnb_url: e.target.value}))}
                          className="form-input text-xs"
                          placeholder="https://www.airbnb.fr/calendar/ical/..."
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          💡 Trouvez ce lien dans : Calendrier Airbnb → Paramètres → Exporter calendrier
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          🔗 URL iCal Booking.com
                        </label>
                        <input
                          type="url"
                          value={formData.ical_booking_url || ''}
                          onChange={(e) => setFormData(prev => ({...prev, ical_booking_url: e.target.value}))}
                          className="form-input text-xs"
                          placeholder="https://admin.booking.com/hotel/..."
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          💡 Trouvez ce lien dans : Extranet Booking → Calendrier → Synchroniser
                        </p>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          🔗 URL iCal Superhôte
                        </label>
                        <input
                          type="url"
                          value={formData.ical_superhote_url || ''}
                          onChange={(e) => setFormData(prev => ({...prev, ical_superhote_url: e.target.value}))}
                          className="form-input text-xs"
                          placeholder="https://..."
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          💡 URL iCal pour Superhôte
                        </p>
                      </div>

                      {formData.ical_last_sync && (
                        <div className="bg-white p-3 rounded border border-purple-200">
                          <p className="text-xs text-gray-600">
                            🕒 Dernière synchronisation : {new Date(formData.ical_last_sync).toLocaleString('fr-FR')}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  📝 Instructions spéciales
                </label>
                <textarea
                  value={formData.instructions}
                  onChange={(e) => setFormData(prev => ({...prev, instructions: e.target.value}))}
                  rows={3}
                  className="form-textarea"
                  placeholder="Instructions particulières pour ce logement..."
                  data-testid="housing-instructions-input"
                />
              </div>

              {/* Lien vers la fiche d'instructions ménage (Google Docs) */}
              <div className="bg-green-50 p-4 rounded-lg border-2 border-green-300 mt-4">
                <label className="block text-sm font-medium text-green-800 mb-2">
                  📄 Lien vers la fiche d'instructions ménage
                </label>
                <input
                  type="url"
                  value={formData.lien_fiche_menage || ''}
                  onChange={(e) => setFormData(prev => ({...prev, lien_fiche_menage: e.target.value}))}
                  className="form-input text-sm"
                  placeholder="https://docs.google.com/document/d/..."
                  data-testid="housing-lien-fiche-menage-input"
                />
                <p className="text-xs text-green-700 mt-2">
                  💡 Collez ici le lien Google Docs contenant les instructions détaillées pour ce logement.
                  Ce lien sera accessible par les intervenants.
                </p>
                {formData.lien_fiche_menage && (
                  <a
                    href={formData.lien_fiche_menage}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center mt-2 text-sm text-green-600 hover:text-green-800 underline"
                  >
                    🔗 Ouvrir la fiche ménage
                  </a>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingHousing(null);
                    resetForm();
                  }}
                  className="btn-secondary"
                  data-testid="cancel-housing-btn"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  data-testid="save-housing-btn"
                >
                  {editingHousing ? "Modifier" : "Créer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression en cascade */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-2xl">
            <div className="text-center mb-4">
              <div className="text-6xl mb-2">⚠️</div>
              <h3 className="text-xl font-bold text-red-600">ATTENTION - Suppression en Cascade</h3>
            </div>
            
            <div className="mb-6 text-gray-700">
              <p className="mb-3 text-center font-semibold">
                Ce logement a <span className="text-red-600 font-bold">{deleteConfirm.activeMissions} mission(s) active(s)</span> 
                {deleteConfirm.totalMissions !== deleteConfirm.activeMissions && 
                  ` (${deleteConfirm.totalMissions} au total)`
                }
              </p>
              
              <div className="bg-red-50 border border-red-200 rounded p-3 mb-3">
                <p className="text-sm">
                  Si vous continuez, le logement <strong>ET TOUTES SES MISSIONS</strong> seront supprimés définitivement.
                </p>
              </div>
              
              <p className="text-center text-red-600 font-bold">
                ⚠️ Cette action est IRRÉVERSIBLE ! ⚠️
              </p>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300"
              >
                ❌ Annuler
              </button>
              <button
                onClick={confirmForceDelete}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 font-bold"
              >
                🗑️ Supprimer Tout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HousingsManager;